import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Card,
  CardContent,
  Divider,
  Grid,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ButtonGroup,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import Drawer from "@mui/material/Drawer";
import axios from "../../../utils/axiosInstance";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import GroupIcon from "@mui/icons-material/Group";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import Diversity3Icon from "@mui/icons-material/Diversity3";
import AppsIcon from "@mui/icons-material/Apps";
import AssignmentIcon from "@mui/icons-material/Assignment";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../ConfirmDialog";
import AssigningCustomerGroupDevice from "./AssigningCustomerGroupDevice";

const GroupDetailView = ({ group, onBack, onViewModeChange }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isTouchDevice = useMediaQuery("(pointer: coarse)");
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  // used to know the size of screen
  const isSmall = useMediaQuery(theme.breakpoints.down("md")); // all that they are not big screen meaning "medium + small screens"
  const isIpadPro = isTouchDevice && isLargeScreen;
  // used to know the size of screen
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentView, setCurrentView] = useState("region");
  const [currentTechnician, setCurrentTechnician] = useState(null);
  const [currentRegion, setCurrentRegion] = useState(null);
  const [regionsData, setRegionsData] = useState([]);
  const [handleRefresh, setHandleRefresh] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [allSystemDevices, setAllSystemDevices] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const fetchUnits = async () => {
      if (!group?.id) return setUnits([]);

      setLoading(true);
      setError(null);

      try {
        // Fetch only group devices (small)
        const groupDevicesResponse = await axios.get(
          `${process.env.REACT_APP_API_GATEAWAY}/devices?groupId=${group.id}`
        );

        const groupDevices = groupDevicesResponse.data || [];

        // Fetch status/type for these devices only
        const deviceIds = groupDevices.map((d) => d.id);
        let deviceStatusMap = {};

        // if (deviceIds.length > 0) {
        //   try {
        //     const { data } = await axios.post(
        //       `${process.env.REACT_APP_API_GATEAWAY}/iot/devices-status-types`,
        //       { regList: ["40001"], devices: deviceIds }
        //     );

        //     data.data.forEach((device) => {
        //       deviceStatusMap[device.id] = {
        //         deviceType: device.type,
        //         status: device.status,
        //       };
        //     });
        //   } catch (statusErr) {
        //     console.error("Error fetching device status/types:", statusErr);
        //   }
        // }

        const groupDevicesWithMetadata = groupDevices.map((device) => ({
          ...device,
          // deviceType: deviceStatusMap[device.id]?.deviceType || null,
          // status: deviceStatusMap[device.id]?.status || null,
        }));

        setUnits(groupDevicesWithMetadata);
        setAllSystemDevices([]); // optional, only if needed
        setCurrentView("all");
      } catch (err) {
        console.error("Error fetching units:", err);
        setError("Failed to load units data");
        setUnits([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUnits();
  }, [group?.id, handleRefresh]);

  const getDevicesSplit = () => {
    // Filter group devices by search term
    const selectedDevices = units.filter(
      (unit) =>
        !searchTerm || // if no search term, include all
        unit.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        unit.id?.toString().includes(searchTerm)
    );

    // Get IDs of devices already in the group
    const groupDeviceIds = new Set(units.map((device) => device.id));

    // Available devices = all system devices NOT in this group
    const availableDevices = allSystemDevices.filter(
      (device) => !groupDeviceIds.has(device.id)
    );

    return {
      selected: selectedDevices,
      available: availableDevices,
    };
  };

  const { selected: filteredDisplayUnits, available: availableDevices } =
    getDevicesSplit();

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const getUnitsTableTitle = () => {
    if (currentView === "technician" && currentTechnician) {
      return `${currentTechnician.name}'s Units`;
    } else if (currentView === "region" && currentRegion) {
      return `${currentRegion.name}: All Units`;
    } else {
      return `${group?.name || "ROOT GROUP"}: Units`;
    }
  };

  const handleAssignOrDelete = () => {
    setShowAssignDialog(true);
  };

  const handleCloseDialog = () => {
    setShowAssignDialog(false);
    // setHandleRefresh(!handleRefresh);
  };

  // Navigation handlers
  const searchUserTable = () => {
    if (onViewModeChange) onViewModeChange("userList");
  };

  const precisionUnitTable = () => {
    if (onViewModeChange) onViewModeChange("PUG");
  };

  const CustomerGroupTable = () => {
    if (onViewModeChange) onViewModeChange("CustomerGroup");
  };

  const ApplicationTable = () => {
    if (onViewModeChange) onViewModeChange("Apps");
  };
  const gotoUnitPage = (id) => {
    navigate(`../device/${id}/engine-data`);
  };
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: isSmall || isIpadPro ? "auto" : "84vh",
        overflow: isSmall || isIpadPro ? "visible" : "hidden",
        minHeight: isSmall || isIpadPro ? "100vh" : "84vh",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: { xs: 0.5, sm: 2, md: 2 },
          mb: 3,
          flexShrink: 0,
          flexDirection: isSmall || isIpadPro ? "column" : "row",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2, md: 2 },
            width: isSmall || isIpadPro ? "100%" : "auto",
          }}
        >
          <IconButton onClick={onBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              fontSize: { xs: "19px", sm: "15px", md: "16px" },
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
            fontWeight="bold"
          >
            Site Admin
          </Typography>
          <Chip
            label={`${group?.name || "ROOT GROUP"}: Regions & Technicians`}
            color="primary"
            size="small"
            sx={{ fontSize: { xs: "9px", sm: "15px", md: "16px" } }}
          />
          <Box
            sx={{
              display: { xs: "flex", sm: "none" },
              justifyContent: "flex-end",
              mb: 1,
            }}
          >
            <IconButton
              onClick={() => setDrawerOpen(true)}
              sx={{
                color: "white",
                bgcolor: "#374151",
                "&:hover": { bgcolor: "#4b5563" },
                borderRadius: 1,
              }}
            >
              <MenuIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Mobile: Drawer Menu */}
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              width: 280,
              bgcolor: "#1f2937",
              color: "white",
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Menu
              </Typography>
              <IconButton
                onClick={() => setDrawerOpen(false)}
                sx={{ color: "white" }}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Button
                fullWidth
                startIcon={<GroupIcon />}
                onClick={() => {
                  searchUserTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "transparent",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#374151",
                  },
                }}
              >
                User List
              </Button>

              <Button
                fullWidth
                startIcon={<AccountTreeIcon />}
                onClick={() => {
                  precisionUnitTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "transparent",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#374151",
                  },
                }}
              >
                Precision Unit Groups
              </Button>

              <Button
                fullWidth
                startIcon={<Diversity3Icon />}
                onClick={() => {
                  CustomerGroupTable();
                  setDrawerOpen(false);
                }}
                
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "#1565c0",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#1565c0",
                  },
                }}
              >
                Customer Groups
              </Button>

              <Button
                fullWidth
                startIcon={<AppsIcon />}
                onClick={() => {
                  ApplicationTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "transparent",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#374151",
                  },
                }}
              >
                Applications
              </Button>

              <Button
                fullWidth
                startIcon={<FactCheckIcon />}
                onClick={() => {
                  AuditTable();
                  setDrawerOpen(false);
                }}
                sx={{
                  justifyContent: "flex-start",
                  px: 2,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "14px",
                  bgcolor: "transparent",
                  color: "white",
                  "&:hover": {
                    bgcolor: "#374151",
                  },
                }}
              >
                Audit
              </Button>
            </Box>
          </Box>
        </Drawer>
      </Box>

      {/* Regions & Technicians Layout */}
      <Grid
        container
        spacing={isSmall || isIpadPro ? 2 : 3}
        sx={{
          height: isSmall || isIpadPro ? "auto" : "calc(100vh - 120px)",
          overflow: isSmall || isIpadPro ? "visible" : "auto",
          pb: isSmall || isIpadPro ? 4 : 0,
        }}
      >
        {/* Units Section */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: isSmall || isIpadPro ? "100%" : "47%",
            height: isSmall || isIpadPro ? "auto" : "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            variant="outlined"
            sx={{
              height: isSmall || isIpadPro ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              mb: isSmall || isIpadPro ? 3 : 0,
            }}
          >
            <CardContent
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflow: isSmall || isIpadPro ? "visible" : "hidden",
                height: { xs: "45vh" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  flexShrink: 0,
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {getUnitsTableTitle()}
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    sx={{
                      borderRadius: 0, // ✅ remove rounding
                    }}
                    variant="outlined"
                    size="small"
                    onClick={() => setHandleRefresh(!handleRefresh)}
                    disabled={loading}
                    startIcon={
                      loading ? (
                        <CircularProgress color="#60a5fa" size={16} />
                      ) : null
                    }
                  >
                    {loading ? "Refreshing..." : "Refresh"}
                  </Button>
                  <Button
                    sx={{
                      borderRadius: 0, // ✅ remove rounding
                    }}
                    variant="contained"
                    size="small"
                    onClick={handleAssignOrDelete}
                    startIcon={<AssignmentIcon />}
                  >
                    Assign or Delete
                  </Button>
                </Box>
              </Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
                  {error}
                </Alert>
              )}

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                  flexShrink: 0,
                }}
              >
                <Typography
                  component="span"
                  sx={{ mr: 1, fontSize: "0.875rem" }}
                >
                  Search:
                </Typography>
                <TextField
                  size="small"
                  sx={{ width: 200, "& .MuiOutlinedInput-root": {
                        borderRadius: 0, // ✅ remove rounding
                      }, }}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  placeholder="Search units..."
                  disabled={loading}
                />
              </Box>

              <TableContainer sx={{ flex: 1, overflow: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "black" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Unit Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Modem Serial Number
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Unit Page
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <CircularProgress color="#60a5fa" size={24} />
                          <Typography sx={{ mt: 1 }}>
                            Loading units...
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : filteredDisplayUnits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                          {searchTerm
                            ? "No units match your search"
                            : "No units available"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredDisplayUnits.map((unit, index) => (
                        <TableRow
                          key={unit.id}
                          hover
                          sx={{
                            backgroundColor:
                              index % 2 === 0 ? "#2f3136" : "#3a3c43",
                            "&:hover": {
                              backgroundColor: "#4a4d54",
                            },
                          }}
                        >
                          <TableCell>{unit.name || "N/A"}</TableCell>
                          <TableCell>{unit.id || "N/A"}</TableCell>
                          <TableCell>
                            {" "}
                            <Button
                              sx={{
                                borderRadius: 0, // ✅ remove rounding
                              }}
                              variant="contained"
                              size="small"
                              onClick={() => gotoUnitPage(unit.id)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="body2"
                sx={{ textAlign: "center", mt: 1, flexShrink: 0 }}
              >
                {loading
                  ? "Loading..."
                  : `Showing ${filteredDisplayUnits.length} of ${units.length} entries`}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Group Info & Units */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            width: isSmall || isIpadPro ? "100%" : "50%", // ✅ Responsive width
            height: isSmall || isIpadPro ? "auto" : "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              height: "100%",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: { xs: "block", sm: "flex", md: "flex" },
                gap: 3,
                flexShrink: 0,
              }}
            >
              {/* Group Information Section */}
              <Paper variant="outlined" sx={{ flex: 1 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Group Information
                    </Typography>
                  </Box>
                  <Box>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell
                              sx={{ fontWeight: "bold", width: "40%" }}
                            >
                              Name
                            </TableCell>
                            <TableCell>{group?.name || "ROOT GROUP"}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Description
                            </TableCell>
                            <TableCell>
                              {group?.description || "ROOT GROUP"}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Date Created
                            </TableCell>
                            <TableCell>
                              {group?.date_created || "2025-10-02T19:10:54"}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </CardContent>
              </Paper>
              {/* Units Section */}
              <Card
                variant="outlined"
                sx={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6" fontWeight="bold">
                      Units
                    </Typography>
                  </Box>
                  <Box>
                    <TableContainer>
                      <Table size="small">
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>
                              Number of Units
                            </TableCell>
                            <TableCell>
                              {loading ? "Loading..." : units.length}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </CardContent>
              </Card>
            </Box>
            <Divider sx={{ flexShrink: 0 }} />
          </Box>
        </Grid>
      </Grid>

      {/* Assign/Delete Dialog */}
      <Dialog
        open={showAssignDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: "80vh",
            maxHeight: "800px",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Manage Devices for {group?.name || "ROOT GROUP"}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <AssigningCustomerGroupDevice
            devices={filteredDisplayUnits} // Currently selected/filtered devices
            allDevicesss={availableDevices} // Devices NOT currently selected
            members={units}
            group={group}
            onRecipientsUpdated={() => {
              setShowAssignDialog(false);
              setHandleRefresh(!handleRefresh);
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GroupDetailView;
